import React, { useCallback, useState } from 'react';
import type { Order } from '@hero/schema';
import { useTranslation } from 'react-i18next';
import { usePublishedOrdersApi } from '@hero/api-hooks';
import { PublishedOrderCard } from './PublishedOrderCard';
import { SearchInput } from '../../../app/Search/SearchInput';
import Spinner from '../../../app/Spinner/Spinner';

export interface PublishedOrderGridProps {
  onOrderClick: (order: Order) => void;
}

export function PublishedOrderGrid({ onOrderClick }: PublishedOrderGridProps) {
  const { t } = useTranslation(['m-order']);
  const [searchTerm, setSearchTerm] = useState('');

  const {
    data: orders,
    isLoading,
    onLoadMore,
    hasNextPage,
    isFetchingNextPage,
  } = usePublishedOrdersApi({});

  const filteredOrders = searchTerm
    ? orders.filter((order: Order) => {
        const ref = order.ref?.short || order.ref?.main || '';
        const search = searchTerm.toLowerCase();
        return ref.toLowerCase().includes(search);
      })
    : orders;

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
      if (scrollHeight - scrollTop - clientHeight < 200 && hasNextPage && !isFetchingNextPage) {
        onLoadMore?.();
      }
    },
    [hasNextPage, isFetchingNextPage, onLoadMore],
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-3">
        <SearchInput
          term={searchTerm}
          setTerm={setSearchTerm}
          placeholder={t('published_orders.search_placeholder')}
        />
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Spinner />
        </div>
      )}

      {!isLoading && filteredOrders.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <p className="text-lg font-medium text-gray-700">{t('published_orders.empty_title')}</p>
          <p className="text-sm text-muted-foreground mt-1">
            {t('published_orders.empty_description')}
          </p>
        </div>
      )}

      {!isLoading && filteredOrders.length > 0 && (
        <div
          className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 max-h-[calc(100vh-220px)] overflow-y-auto"
          onScroll={handleScroll}>
          {filteredOrders.map((order: Order) => (
            <PublishedOrderCard key={order._id?.toString()} order={order} onClick={onOrderClick} />
          ))}
          {isFetchingNextPage && (
            <div className="col-span-full flex justify-center py-4">
              <Spinner />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
